import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAccountStore } from '../../stores/account';
import { useMotionTier } from '../../components/useMotionTier';
import { Cassette } from '../../components/procedural/Cassette';
import { duration, ease } from '../../theme/motion';

// Studio splash. First-visit only — returning players (hasPlayed === true)
// bounce straight to /welcome with no flash. Tap-anywhere skips.
//
// The cassette here is the SAME component the Welcome → Menu tape transport
// mounts later (components/procedural/Cassette). That is the point: the whole
// first-run arc is one tape, still running. The old splash drew its own
// throwaway cassette and cross-faded it against an unrelated title fade, so
// nothing carried across the cut.
//
// On exit the reels keep turning and the body pushes toward the viewer rather
// than dimming out — the tape isn't stopping, we're just moving past it.
//
// The hold is shorter than it used to be (2000ms). Now that the exit hands off
// into Welcome instead of dissolving to nothing, the hold doesn't have to
// carry the whole brand moment on its own.

const HOLD_MS = 1600;
const FADE_OUT_MS = 350;

/** Reel speed, rotations per second. Slow idle — this is a tape at rest. */
const IDLE_RATE = 0.5;
/** Spin-up on exit, so the hand-off reads as the transport engaging. */
const EXIT_RATE = 1.6;

export function SplashPage() {
  const navigate = useNavigate();
  const hasPlayed = useAccountStore((s) => s.hasPlayed);
  const tier = useMotionTier();
  const [exiting, setExiting] = useState(false);
  const reduced = tier === 'reduced';

  // First-visit gate. `replace` so the back button doesn't return the player
  // to a splash they didn't ask for.
  useEffect(() => {
    if (hasPlayed) {
      navigate('/welcome', { replace: true });
    }
  }, [hasPlayed, navigate]);

  useEffect(() => {
    if (hasPlayed) return;
    const exitTimer = setTimeout(() => setExiting(true), HOLD_MS);
    const navTimer = setTimeout(
      () => navigate('/welcome', { replace: true }),
      HOLD_MS + FADE_OUT_MS,
    );
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(navTimer);
    };
  }, [hasPlayed, navigate]);

  useEffect(() => {
    function skip() {
      navigate('/welcome', { replace: true });
    }
    window.addEventListener('pointerdown', skip);
    window.addEventListener('keydown', skip);
    return () => {
      window.removeEventListener('pointerdown', skip);
      window.removeEventListener('keydown', skip);
    };
  }, [navigate]);

  if (hasPlayed) return null;

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* Soft sodium wash — single source, off-centre, doesn't compete with the mark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 60%, rgba(255,138,61,0.10) 0%, rgba(255,138,61,0) 65%)',
        }}
      />

      <section className="relative flex h-full w-full flex-col items-center justify-center gap-lg">
        <motion.div
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
          animate={
            exiting
              ? // Push past the tape rather than dimming it out. The reels are
                // still turning as it goes.
                { opacity: 0, scale: reduced ? 1 : 1.18, y: 0 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          transition={{
            duration: exiting ? FADE_OUT_MS / 1000 : duration.settle,
            ease: exiting ? ease.in : ease.out,
          }}
          className="w-[180px] md:w-[240px]"
        >
          <Cassette
            rate={reduced ? 0 : exiting ? EXIT_RATE : IDLE_RATE}
            tier={tier}
            className="h-auto w-full"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: exiting ? 0 : 1 }}
          transition={{
            duration: exiting ? FADE_OUT_MS / 1000 : duration.settle,
            delay: exiting ? 0 : 0.15,
            ease: ease.out,
          }}
          className="flex flex-col items-center gap-xs"
        >
          <p className="font-data text-[10px] uppercase tracking-[0.4em] text-graphite md:text-[12px]">
            serifeus studio
          </p>
          <p className="font-data text-[8px] uppercase tracking-[0.5em] text-graphite/60 md:text-[10px]">
            presents
          </p>
        </motion.div>
      </section>
    </main>
  );
}
